import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import HistoryFilters from "./HistoryFilters";
import { useNavigate } from "react-router-dom";
import { LoadRequestHistoryItem, LoadRequestHistoryParams, LoadRequestHistoryResponse } from "./types";
import { getLoadRequestHistory } from "apis/myRequestsService";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import SecondHeader from "../../assets/images/top-header-2.png";

interface RequestHistoryProps {
  onBack: () => void;
}

interface FilterState {
  requestedFromDate?: string;
  requestedToDate?: string;
  answeredFromDate?: string;
  answeredToDate?: string;
  status?: string;
}

const RequestHistory: React.FC<RequestHistoryProps> = ({ onBack }) => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [history, setHistory] = useState<LoadRequestHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [favourites, setFavourites] = useState<number[]>([]);
  const [filters, setFilters] = useState({
    requestedFromDate: "",
    requestedToDate: "",
    answeredFromDate: "",
    answeredToDate: "",
    status: "",
  });

  // Debounce search input to reduce unnecessary API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery); // Update only after debounce delay
    }, 500); // Adjust debounce delay if needed

    return () => clearTimeout(handler); // Cleanup previous timer
  }, [searchQuery]);

  // Reset to page 1 when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  // Fetch history when page, rowsPerPage, search query, or filters change
  useEffect(() => {
    fetchHistory(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, debouncedSearch, filters]);

  const fetchHistory = async (page: number, pageSize: number) => {
    setLoading(true);
    try {
      const params: LoadRequestHistoryParams = {
        page,
        pageSize,
        flightNameOrNumber: debouncedSearch.trim() || undefined, // Send search param if available
        ...filters, // Include filters in the API request
      };

      const data: LoadRequestHistoryResponse = await getLoadRequestHistory(params);
      setHistory(data.items);
      setTotalCount(data.totalCount);
      setTotalPages(Math.ceil(data.totalCount / pageSize));
    } catch (err) {
      setError("Failed to fetch history");
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const handleApplyFilters = (newFilters: any) => {
    setFilters(newFilters);
    // The useEffect will automatically trigger a fetch with the new filters
  };

  const toggleFavourite = (id: number) => {
    setFavourites(
      (prev) =>
        prev.includes(id)
          ? prev.filter((item) => item !== id) // remove
          : [...prev, id] // add
    );
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  useClickOutside(dropdownRef, () => setIsFilterOpen(false));

  return (
    <div className="">
      {/* Previous JSX remains the same until the table */}
      <section className="flex flex-wrap items-center gap-3 sm:gap-4 justify-between lg:h-[125.5px] bg-cover p-4 sm:p-6">
        <p className="text-black text-lg font-semibold">Enquiries</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <i className="st-home text-[#262626] text-xs font-extralight"></i>
            <a className="text-[#262626] cursor-pointer font-extralight" onClick={() => navigate("/search")}>
              Home
            </a>
            <i className="st-arrow-right text-[#262626] text-xs"></i>
            <i className="st-request text-[#262626] text-xs"></i>
            <a className="text-[#262626] cursor-pointer font-extralight" onClick={onBack}>
              Enquiries
            </a>
            <i className="st-arrow-right text-[#262626] text-xs"></i>
          </div>
          <p className="text-[#262626] text-sm font-bold cursor-pointer">History</p>
        </div>
      </section>
      <section>
        <div className="bg-white rounded-lg p-3 sm:p-4 mt-2 lg:-mt-10 mx-3 sm:mx-4">
          {/* Previous filter and search section remains the same */}
          <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
            <div className="flex items-center gap-2">
              <p className="text-grease-100 text-sm font-semibold">History</p>
              {totalCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-lightblue-100 text-grease-100 text-xs font-medium">
                  {totalCount} {totalCount === 1 ? "record" : "records"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 relative">
              <div className="relative">
                <input
                  className="block w-full p-3 pl-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                  placeholder="Search by keyword"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="absolute w-fit inset-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none">
                  <i className="st-search text-lightgray-200"></i>
                </button>
              </div>
              <div className="relative">
                <button
                  id="dropdownDefaultButton-3"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="flex items-center gap-2 p-3 border bg-[#FFBC00] rounded-lg"
                >
                  <i className="st-filter text-[#F5F9FE]"></i>
                  <p className="font-medium text-[#F5F9FE] text-sm">Filters</p>
                </button>

                {isFilterOpen && (
                  <div ref={dropdownRef} className="absolute right-0 max-sm:left-0 max-sm:right-auto origin-top-right mt-2 max-w-[calc(100vw-1rem)] shadow-lg border border-gray-200 rounded-lg z-50">
                    <HistoryFilters
                      initialFilters={filters}
                      closeDropdown={() => setIsFilterOpen(false)}
                      onApplyFilters={handleApplyFilters}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results (mobile cards + desktop table) with a loading overlay */}
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 rounded-lg">
                <div className="w-7 h-7 border-2 border-[#FFBC00] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

          {/* Mobile card list (hidden on sm+) */}
          <div className="sm:hidden flex flex-col gap-3">
            {history.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-8">
                History not found
              </p>
            ) : (
              history.map((row, index) => {
                const statusColor =
                  row?.status === "Expired"
                    ? "bg-red-100 text-red-600"
                    : row?.status === "Answered"
                      ? "bg-green-100 text-green-600"
                      : row?.status === "Pending"
                        ? "bg-[#FEF3C7] text-[#D97706]"
                        : "bg-gray-100 text-gray-600";
                return (
                  <div
                    key={index}
                    className="bg-[#F8F9FC] border border-[#EDEEF1] rounded-xl p-3"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-[#1F2937] text-sm font-semibold truncate">
                          {row?.flightName || "—"}
                        </p>
                        <p className="text-[#9CA3AF] text-xs mt-0.5">
                          {row?.flightNumber || "—"}
                        </p>
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${statusColor}`}
                      >
                        {row?.status || "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-[#1F2937] text-sm font-bold">
                        {row?.departureAirport || "—"}
                      </p>
                      <i className="st-arrow-right text-[#9CA3AF] text-xs"></i>
                      <p className="text-[#1F2937] text-sm font-bold">
                        {row?.destinationAirport || "—"}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-[#9CA3AF]">Requested</p>
                        <p className="text-[#1F2937] mt-0.5">
                          {row?.requestedDate
                            ? new Date(row.requestedDate).toLocaleDateString(
                                "en-GB",
                              )
                            : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#9CA3AF]">Answered</p>
                        <p className="text-[#1F2937] mt-0.5">
                          {row?.status === "Expired" || !row?.answeredDate
                            ? "N/A"
                            : new Date(row.answeredDate).toLocaleDateString(
                                "en-GB",
                              )}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Table section (desktop only) */}
          <div className="relative overflow-x-auto sm:rounded-lg hidden sm:block">
            <table className="w-full text-sm text-left text-gray-500">
              {/* Table header remains the same */}
              <thead className="text-xs text-gray-700 p-4 bg-blue-50">
                <tr>
                  <th scope="col" className="px-6 py-4 rounded-l-lg">
                    <p className="text-lightgray-200 text-left">Sl.No.</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 text-left px-4">Flight Number</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 text-left px-4">Flight Name</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 text-left px-4">Requested Date</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 text-left px-4">Status</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 text-left px-4">Answered Date</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 text-left px-4">From</p>
                  </th>
                  <th scope="col" className="px-6 py-4 rounded-r-lg">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 text-left px-4">To</p>
                  </th>
                </tr>
                <tr>
                  <td className="h-2 bg-white"></td>
                </tr>
              </thead>
              <tbody className="rounded-lg">
                {history.length > 0 &&
                  history?.map((row, index) => (
                    <tr key={index} className="odd:bg-white even:bg-blue-50 border">
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p>{(currentPage - 1) * rowsPerPage + index + 1}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p className="px-4">{row?.flightNumber}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p className="px-4">{row?.flightName}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p className="px-4">{row?.requestedDate ? new Date(row.requestedDate).toLocaleDateString("en-GB") : ""}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p className="px-4">{row?.status}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p className="px-4">{row.status === "Expired" ? "N/A" : new Date(row.answeredDate).toLocaleDateString("en-GB")}</p>
                      </td>

                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p className="px-4">{row?.departureAirport}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 text-left whitespace-nowrap">
                        <p className="px-4">{row?.destinationAirport}</p>
                      </td>
                      {/* <td className="px-6 py-4 text-center">
                        <button onClick={() => toggleFavourite(row.id)} className="p-2 rounded-full hover:bg-gray-100 transition">
                          {favourites.includes(row.id) ? (
                            <i className="st-heart text-red-500 text-lg"></i> // filled heart
                          ) : (
                            <i className="st-heart text-gray-400 text-lg"></i> // empty heart
                          )}
                        </button>
                      </td> */}
                    </tr>
                  ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      History not found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>

          {/* Updated pagination section */}
          <div className="flex flex-row flex-wrap items-center justify-between gap-3 mt-4">
            <div className="flex items-center gap-2 justify-start order-2 sm:order-1">
              <p className="text-lightgray-200 text-sm whitespace-nowrap">Rows per page </p>
              <div className="relative inline-flex items-center">
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    handleRowsPerPageChange(Number(e.target.value));
                    // Drop focus so the select doesn't stay highlighted after
                    // the list reloads (matches the other paginated pages).
                    e.target.blur();
                  }}
                  className="appearance-none border-none pl-4 pr-9 py-2 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none cursor-pointer"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                </select>
                {/* <i className="st-arrow-down text-grease-100 text-[10px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"></i> */}
              </div>
            </div>
            <div className="flex justify-center sm:justify-end order-1 sm:order-2">
              <ul className="flex flex-wrap items-center justify-center my-0 gap-2 sm:gap-3 text-sm">
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center w-8 h-8 leading-tight text-primaryblue-50 border border-primaryblue-50 rounded-md disabled:opacity-50"
                  >
                    <i className="st-arrow-right text-2xl font-extrabold rotate-180"></i>
                  </button>
                </li>
                {getPageNumbers().map((page) => (
                  <li key={page}>
                    <button
                      onClick={() => handlePageChange(page)}
                      className={`flex items-center justify-center w-8 h-8 rounded-md ${
                        currentPage === page
                          ? "text-white border border-primaryblue-50 bg-primaryblue-50 font-extrabold"
                          : "text-primaryblue-50 border border-primaryblue-50"
                      }`}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center w-8 h-8 text-primaryblue-50 border border-primaryblue-50 rounded-md disabled:opacity-50"
                  >
                    <i className="st-arrow-right text-2xl font-extrabold"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RequestHistory;
